import {
  Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import { TableColumn } from '@swimlane/ngx-datatable';
import { LookupDialogService } from './lookup-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

export class LookupRequestInfo {
  pageType: string;
  entitySubType?: string;
  pageSize: number = 50;
  currentPageIndex: number = 1;
  entityViewId: string;
  refresh?: boolean = false;
  filter?: string = '';
  isDescendingSort?: boolean;
  sortColumnName?: string;
  entityType: number;
  includeLookupViews: boolean;
  editorType?: string;

  constructor(lookupRequestInfo: LookupRequestInfo) {
    this.pageType = lookupRequestInfo.pageType;
    this.entitySubType = lookupRequestInfo.entitySubType;
    this.pageSize = lookupRequestInfo.pageSize;
    this.currentPageIndex = lookupRequestInfo.currentPageIndex;
    this.entityViewId = lookupRequestInfo.entityViewId;
    this.refresh = lookupRequestInfo.refresh;
    this.filter = lookupRequestInfo.filter;
    this.isDescendingSort = lookupRequestInfo.isDescendingSort;
    this.sortColumnName = lookupRequestInfo.sortColumnName;
    this.entityType = lookupRequestInfo.entityType;
    this.includeLookupViews = lookupRequestInfo.includeLookupViews;
    this.editorType = lookupRequestInfo.editorType;
  }
}

@Component({
  selector: 'grid-component',
  templateUrl: 'lookup-grid.html'
})

export class LookupGridComponent implements OnChanges {
  @ViewChild('checkboxTmpl', {static: false}) checkboxTmpl: TemplateRef<any>;
  @Output() loadingChanged: EventEmitter<any> = new EventEmitter();
  @Output() selectedRecordsChanged: EventEmitter<any> = new EventEmitter();
  @Output() columnsChanged: EventEmitter<any> = new EventEmitter();
  @Input() isLoading: boolean = false;
  @Input() requestInfo: LookupRequestInfo;
  @Input() selectionType: string;
  @Input() entityName: string;
  @Input() changedRequestInfoAttribute: string;

  cssClasses: any = {
    sortAscending: 'datatable-sort-asc',
    sortDescending: 'datatable-sort-desc'
  };

  get messages(): any {
    return {
      emptyMessage: `
        <div class="grid-no-message">
        <span>${this.gridTextNo}</span>
        <span class="grid-no-message-entity-name">${this.entityName}</span>
        <span>${this.gridTextNoResults}</span>.</div>`
    };
  }

  get pageSize(): number {
    if (!this.requestInfo || !this.requestInfo.pageSize) {
      return 1;
    }
    return this.requestInfo.pageSize;
  }

  set pageSize(value: number) {
    this.requestInfo.pageSize = value;
  }

  get currentPageIndex(): number {
    if (!this.requestInfo || !this.requestInfo.currentPageIndex) {
      return 0;
    }
    return this.requestInfo.currentPageIndex;
  }

  set currentPageIndex(value: number) {
    this.requestInfo.currentPageIndex = value;
  }

  get selectedCount(): number {
    if (!this.selected) {
      return 0;
    }
    return this.selected.length;
  }

  get currPageRecordsCount(): number {
    if (!this.rows) {
      return 0;
    }
    return this.rows.length;
  }

  totalRecords: number = 0;
  rows: any[] = [];

  fieldList: any[] = [];
  columns: TableColumn[] = [];
  selected: any[] = [];

  offsetFirstItem: number = 0;
  offsetLastItem: number = 0;
  maxRows: number = 0;
  maxPages: number = 0;
  getRecordsRequest: any;
  gridTextNo: string;
  gridTextNoResults: string;

  constructor(private lookupDialogService: LookupDialogService, private translate: TranslateService) {
    this.gridTextNo = translate.instant('COMMON.DIALOGS.LOOKUP.GRID_TEXT_NO');
    this.gridTextNoResults = translate.instant('COMMON.DIALOGS.LOOKUP.GRID_TEXT_NO_RESULTS');
  }

  cantPageBackward() {
    return !(this.requestInfo.currentPageIndex > 1);
  }

  cantPageForward() {
    return !(this.requestInfo.currentPageIndex < this.maxPages);
  }

  pageToFirst() {
    if (this.requestInfo.currentPageIndex !== 1) {
      this.requestInfo.currentPageIndex = 1;
      this.initGrid();
    }
  }

  pageBackward() {
    const prevPageIndex = Math.max(this.requestInfo.currentPageIndex - 1, 1);
    if (prevPageIndex < this.requestInfo.currentPageIndex) {
      this.requestInfo.currentPageIndex = prevPageIndex;
      this.initGrid();
    }
  }

  pageForwardToNext() {
    if (this.offsetLastItem >= this.totalRecords) {
      return;
    }
    const nextPageIndex = Math.min(this.requestInfo.currentPageIndex + 1, this.maxPages);
    if (nextPageIndex > this.requestInfo.currentPageIndex) {
      this.requestInfo.currentPageIndex = nextPageIndex;
      this.initGrid();
    }
  }

  isToResetGrid(changes: SimpleChanges) {
    if (changes.lookupName && changes.lookupName.currentValue !== changes.lookupName.previousValue) {
      return true;
    }
    return changes.changedRequestInfoAttribute
      && changes.changedRequestInfoAttribute.currentValue !== changes.changedRequestInfoAttribute.previousValue
      && changes.changedRequestInfoAttribute.currentValue !== '';

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isToResetGrid(changes)) {
      this.resetGrid();
    }
    if (changes.requestInfo && changes.requestInfo.currentValue.entityViewId !== '' &&
      changes.requestInfo.currentValue.entityViewId !== null) {
      this.initGrid();
    }
  }

  getId(row: any) {
    return row.Id;
  }

  initGrid() {
    if (this.requestInfo.entityViewId !== '' && this.requestInfo.entityViewId !== null) {
      this.setPage();
    }
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
    setTimeout(() => {
      this.loadingChanged.emit(this.isLoading);
    }, 0);
  }

  setPage() {
    this.setLoading(true);

    if (this.getRecordsRequest) {
      this.getRecordsRequest.unsubscribe();
    }

    this.getRecordsRequest = this.lookupDialogService.getRecords(this.requestInfo)
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe((data) => this.handleGetRecordsResult(data),
        (err) => {
          this.handleGetRecordsError(err);
        });
  }

  refresh() {
    this.requestInfo.refresh = true;
    this.initGrid();
  }

  resetGrid(): void {
    this.columns = [];
    this.fieldList = [];
    this.selected = [];
    this.offsetFirstItem = 0;
    this.offsetLastItem = 0;
    this.maxRows = 0;
    this.maxPages = 0;
    this.totalRecords = 0;
    this.requestInfo.currentPageIndex = 1;
  }

  handleGetRecordsResult(res: any) {
    if (res) {
      this.fieldList = res.fieldList;
      this.createColumns(res.fieldList);

      this.columnsChanged.emit(this.fieldList);

      if (this.requestInfo.currentPageIndex === 1) {
        this.totalRecords = res.totalRecords;
      }

      this.rows = Object.assign([], res.entityRows);

      const offsetFirstItem = (this.requestInfo.currentPageIndex - 1) * (this.requestInfo.pageSize) + 1;
      this.offsetFirstItem = Math.min(offsetFirstItem, this.totalRecords);
      this.maxRows = Math.max(this.totalRecords || this.rows.length, 1);
      const offsetLastItems = this.requestInfo.currentPageIndex * this.requestInfo.pageSize;
      this.offsetLastItem = Math.min(offsetLastItems, this.totalRecords);
      this.maxPages = Math.ceil(this.maxRows / this.requestInfo.pageSize);
    }
  }

  handleGetRecordsError(error: any) {
    console.log(`error in get records from server: ${error.originalError || error}`);
  }

  onSort(event: any) {
    this.isLoading = true;
    this.requestInfo.isDescendingSort = event.newValue === 'desc';
    this.requestInfo.sortColumnName = event.column.prop;
    this.requestInfo.currentPageIndex = 1;
    this.setPage();
  }

  onSelect(selectedItems: any) {
    this.selected.splice(0, this.selected.length);
    if (selectedItems.selected && selectedItems.selected.length > 0) {
      this.selected.push(...selectedItems.selected);
    }

    this.selectedRecordsChanged.emit(this.selected);
  }

  clearSelected(){
    this.selected = [];
  }

  createColumns(columns: any[]) {
    if (this.columns.length === 0) {
      const checkboxColumn: TableColumn = {
        // this column acts as a spacer for the check icon. there is no real value in showing the id to the user.
        cellTemplate: this.checkboxTmpl, prop: 'Id', name: ' ', width: 30, minWidth: 30, maxWidth: 30
      };

      this.columns = [];
      this.columns.push(checkboxColumn);

      if (typeof columns !== 'undefined' && columns != null && columns.length > 0) {
        for (let i = 0; i < columns.length; i++) {
          if (columns[i].visible) {
            const column: TableColumn = {
              prop: columns[i].field,
              name: columns[i].displayName,
              sortable: columns[i].sortable
            };

            if (columns[i].field !== 'Id') {
              // hide the id column, since we are not getting data into it
              // and the user does not care about it anyway
              this.columns.push(column);
            }
          }
        }
      }
    }
  }
}
